import { ApiOkResponse, ApiResponse, ApisauceInstance, create, ResponseTransform } from 'apisauce';
import { AxiosRequestConfig } from 'axios';
import { Observable } from 'rxjs';
import { flatMap, map } from 'rxjs/operators';
import { from$ } from '~domain/helper/rxs';
import { BaseRequestErrorHandler, RequestErrorHandler } from './request-error-handlers/base-request-error-handler';

export type QueryParams = Record<string, unknown>;

export type { ApiResponse, ResponseTransform };

export type Headers = Record<string, number | string | string[] | undefined>;

export interface UploadCompletedEvent {
  data: unknown;
}

export type UploadEvent = UploadCompletedEvent;

export const DEFAULT_JSON_API_HEADERS: Headers = {
  Accept: 'application/json',
  'Content-Type': 'application/json',
};

export class BaseClient {
  public api: ApisauceInstance;
  private requestErrorHandler: RequestErrorHandler;

  constructor(
    baseUrl: string,
    headers: Headers,
    // Create a base request error handler if none is passed
    // This gives the option to handle errors differently
    // from the default way
    requestErrorHandler: RequestErrorHandler = new BaseRequestErrorHandler(),
  ) {
    this.api = create({ baseURL: baseUrl, headers });
    this.requestErrorHandler = requestErrorHandler;
  }

  public addMonitor(monitor: any) {
    this.api.addMonitor(monitor);
  }

  /**
   * Perform an HTTP `POST` and transforms response as an `Object` and
   * throw when server error or body is not an `Object`.
   * Headers passed here are not permanents. They are setted only for the current request
   */
  protected create$<T>(
    url: string,
    body?: any,
    params?: QueryParams,
    headers?: Headers,
    options?: { responseType?: 'arraybuffer' | 'json' | 'text' },
  ): Observable<T>;

  protected create$<T>(
    url: string,
    body: any,
    params: QueryParams,
    headers: Headers,
    options: { return: 'response'; responseType?: 'arraybuffer' | 'json' | 'text' },
  ): Observable<ApiOkResponse<T>>;

  protected create$<T>(
    url: string,
    body?: any,
    params?: QueryParams,
    headers?: Headers,
    options?: { return?: 'response'; responseType?: string },
  ): Observable<T | ApiOkResponse<T>> {
    return this.post$<T>(url, null, { data: body, params, headers, responseType: options?.responseType }).pipe(
      map((response) => {
        if (options?.return === 'response') {
          if (!response.ok) {
            handleError(response, this.requestErrorHandler);
            return;
          }

          return response;
        }

        return this.extractResponseBody(response);
      }),
    );
  }

  /**
   * Perform an HTTP `DELETE` and transforms response as an `Object` and
   * throw when server error or body is not an `Object`.
   */
  protected delete$<T>(url: string, body?: unknown, params?: QueryParams, headers?: Headers): Observable<T> {
    return this.del$<T>(url, params, { data: body, headers }).pipe(
      map((response) => this.extractResponseBody(response)),
    );
  }

  /**
   * Perform an HTTP `GET` and transforms response as an `Object` and
   * throw when server error or body is not an `Object`.
   */
  protected fetch$<T>(url: string, params?: QueryParams, axiosConfig?: AxiosRequestConfig): Observable<T> {
    return this.get$<T>(url, params, axiosConfig).pipe(map((response) => this.extractResponseBody(response)));
  }

  /**
   * Perform an HTTP `GET` and transforms response as a stream of `Object` and
   * throw when server error or body is not an array of `Object`.
   */
  protected list$<T>(url: string, params?: QueryParams): Observable<T> {
    return this.get$<T[]>(url, params).pipe(flatMap((response) => this.responseToList$(response)));
  }

  /**
   * Perform an HTTP `PUT` and transforms response as an `Object` and
   * throw when server error or body is not an `Object`.
   */
  protected update$<T>(url: string, body?: unknown, queryParams?: QueryParams, headers?: Headers): Observable<T> {
    return this.put$<T>(url, null, { data: body, params: queryParams, headers }).pipe(
      map((response) => this.extractResponseBody(response)),
    );
  }

  /**
   * Perform an HTTP `PATCH` and transforms response as an `Object` and
   * throw when server error or body is not an `Object`.
   */
  protected updatePartially$<T>(
    url: string,
    body?: unknown,
    queryParams?: QueryParams,
    headers?: Headers,
  ): Observable<T> {
    return this.patch$<T>(url, null, { data: body, params: queryParams, headers }).pipe(
      map((response) => this.extractResponseBody(response)),
    );
  }

  protected upload$<T>(path: string, form: any, config: AxiosRequestConfig = {}): Observable<UploadEvent> {
    const headers: Headers = { ...config.headers };
    headers['Content-Type'] = 'multipart/form-data';

    return this.post$<T>(path, form, { ...config, headers }).pipe(
      map((response) => this.extractResponseBody(response)),
      map((body: T) => ({ data: body })),
    );
  }

  private del$<T>(url: string, params: QueryParams, axiosConfig?: AxiosRequestConfig): Observable<ApiResponse<T>> {
    return from$(this.api.delete<T>(url, params, axiosConfig));
  }

  private get$<T>(url: string, params?: QueryParams, axiosConfig?: AxiosRequestConfig): Observable<ApiResponse<T>> {
    return from$(this.api.get<T>(url, params, axiosConfig));
  }

  private patch$<T>(url: string, data?: unknown, params?: AxiosRequestConfig): Observable<ApiResponse<T>> {
    return from$(this.api.patch<T>(url, data, params));
  }

  private post$<T>(url: string, data?: unknown, axiosConfig?: AxiosRequestConfig): Observable<ApiResponse<T>> {
    return from$(this.api.post<T>(url, data, axiosConfig));
  }

  private put$<T>(url: string, data?: unknown, params?: QueryParams): Observable<ApiResponse<T>> {
    return from$(this.api.put<T>(url, data, params));
  }

  private extractResponseBody<T>(response: ApiResponse<T>): T {
    if (!response.ok) {
      handleError(response, this.requestErrorHandler);
      return;
    }

    return response.data;
  }

  /**
   * Transforms the response body assumed to be an `array` into
   * an Observable stream containing all array elements:
   *   `responseToList$({ data: [1, 2 , 3]}) == '|----1----2----3----.'`
   */
  private responseToList$<T>(response: ApiResponse<T[]>): Observable<T> {
    const body = extractResponseBody<T[]>(response, this.requestErrorHandler);
    if (!Array.isArray(body)) {
      throw new Error(`Expected response to be an array, received data of type [${typeof body}].`);
    }

    return from$(body);
  }
}

function extractResponseBody<T>(response: ApiResponse<T>, requestErrorHandler: RequestErrorHandler): T {
  if (!response.ok) {
    handleError(response, requestErrorHandler);
    return;
  }

  return response.data;
}

function handleError<T>(response: ApiResponse<T>, requestErrorHandler: RequestErrorHandler): void {
  const { problem } = response;

  requestErrorHandler.handleRequestError(problem, response);
}
